import { useState, useRef, useEffect, useCallback } from 'react';
import { router, Link } from '@inertiajs/react';
import { ShoppingCart } from 'lucide-react';
import { route } from '../../utils/route';
import type { CategoryWithCount } from '../../types/products';
import { CategoryIcon, SearchIcon, CloseIcon } from './categoryIcons';

interface MobileToolbarProps {
  categories: CategoryWithCount[];
  activeCategoryId: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** When provided, pressing Enter submits search */
  onSearchSubmit?: (term: string) => void;
  /** Called when search is cleared/closed */
  onSearchClear?: () => void;
}

export function MobileToolbar({
  categories,
  activeCategoryId,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onSearchClear,
}: MobileToolbarProps) {
  const [searchOpen, setSearchOpen] = useState(() => !!searchValue);
  const [cartCount, setCartCount] = useState(0);
  const [formatSheetHeight, setFormatSheetHeight] = useState(0);
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync cart count from sessionStorage + listen for updates
  useEffect(() => {
    const readFromStorage = () => {
      try {
        const cached = sessionStorage.getItem('cart_count');
        setCartCount(cached ? parseInt(cached, 10) || 0 : 0);
      } catch {}
    };
    readFromStorage();

    const handler = (e: Event) => {
      // Read count from event detail if available (avoids race condition)
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail.count === 'number') {
        setCartCount(detail.count);
      } else {
        // Fallback to sessionStorage (already updated by the caller)
        readFromStorage();
      }
    };
    window.addEventListener('cart-updated', handler);
    return () => window.removeEventListener('cart-updated', handler);
  }, []);

  // Clear pending state when navigation completes (activeCategoryId changes)
  useEffect(() => {
    setPendingSlug(null);
  }, [activeCategoryId]);

  useEffect(() => {
    if (searchOpen && !searchValue) {
      // Focus only when opening fresh (not pre-populated)
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [searchOpen]);

  // Track format bottom sheet to position toolbar above it
  useEffect(() => {
    const onOpen = (e: Event) => {
      const h = (e as CustomEvent).detail?.height ?? 0;
      setFormatSheetHeight(h);
    };
    const onClose = () => setFormatSheetHeight(0);
    window.addEventListener('format-sheet-open', onOpen);
    window.addEventListener('format-sheet-close', onClose);
    return () => {
      window.removeEventListener('format-sheet-open', onOpen);
      window.removeEventListener('format-sheet-close', onClose);
    };
  }, []);

  // iOS: keep toolbar anchored to the visual viewport bottom when keyboard opens
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    let rafPending = false;
    const update = () => {
      // Throttle with requestAnimationFrame to avoid rapid state updates
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        const offset = window.innerHeight - vv.height - vv.offsetTop;
        setKeyboardOffset(offset > 50 ? offset : 0);
        rafPending = false;
      });
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update, { passive: true });
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  const handleSearchToggle = useCallback(() => {
    window.dispatchEvent(new Event('format-sheet-dismiss'));
    if (searchOpen) {
      onSearchChange('');
      onSearchClear?.();
      setSearchOpen(false);
    } else {
      setSearchOpen(true);
    }
  }, [searchOpen, onSearchChange, onSearchClear]);

  const handleCategoryClick = useCallback((slug: string) => {
    window.dispatchEvent(new Event('format-sheet-dismiss'));
    if (searchOpen) {
      onSearchChange('');
      setSearchOpen(false);
    }
    // Optimistic UI: show button as active immediately
    setPendingSlug(slug);
    router.visit(route('products.index', { cat: slug }), { preserveScroll: false });
  }, [searchOpen, onSearchChange]);

  const handleSearchFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim().length >= 2) {
      onSearchSubmit?.(searchValue.trim());
      inputRef.current?.blur();
    }
  }, [searchValue, onSearchSubmit]);

  const visibleCategories = categories.filter(c => c.slug !== 'ocultos');

  const bottomOffset = Math.max(formatSheetHeight, keyboardOffset);

  return (
    <div
      ref={containerRef}
      className="sm:hidden fixed left-0 right-0 z-[2147483001] pb-[env(safe-area-inset-bottom)] transition-[bottom] duration-[250ms] ease-out"
      style={{ bottom: bottomOffset > 0 ? `${bottomOffset}px` : 0 }}
    >
      <div className="mx-2 mb-2 rounded-2xl border border-gray-200 bg-white/95 backdrop-blur-sm shadow-lg overflow-hidden">
        <div className="flex items-center gap-1 px-3 py-2">
          {/* Search button — always visible */}
          <button
            type="button"
            onClick={handleSearchToggle}
            className={`flex-shrink-0 flex items-center justify-center rounded-xl h-11 w-11 transition-colors duration-200 ${
              searchOpen
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600 active:bg-gray-200'
            }`}
            aria-label={searchOpen ? 'Cerrar búsqueda' : 'Buscar'}
          >
            {searchOpen ? <CloseIcon size={20} /> : <SearchIcon size={20} />}
          </button>

          {/* Categories + cart OR search input */}
          <div className="flex-1 min-w-0">
            {searchOpen ? (
              <form onSubmit={handleSearchFormSubmit} action="#">
                <input
                  ref={inputRef}
                  type="search"
                  enterKeyHint="search"
                  autoComplete="off"
                  autoCorrect="off"
                  className="w-full h-11 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-gray-800 placeholder-gray-400 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                  placeholder="Buscá frutas, verduras y más..."
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </form>
            ) : (
              <div className="flex items-center gap-1">
                <div className="flex-1 flex justify-center gap-2">
                  {visibleCategories.map(cat => {
                    // Use pending slug for optimistic UI, fallback to actual active category
                    const isActive = pendingSlug ? cat.slug === pendingSlug : cat.id === activeCategoryId;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleCategoryClick(cat.slug)}
                        className={`flex items-center justify-center rounded-xl w-11 h-11 transition-colors ${
                          isActive
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                        }`}
                        aria-label={cat.name}
                      >
                        <CategoryIcon slug={cat.slug} size={20} strokeWidth={2} />
                      </button>
                    );
                  })}
                </div>

                {/* Cart button - green when on /carrito, gray otherwise */}
                {(() => {
                  const isCartPage = typeof window !== 'undefined' && window.location.pathname === '/carrito';
                  return (
                    <Link
                      href={route('cart.review')}
                      className={`flex-shrink-0 relative flex items-center justify-center rounded-xl w-11 h-11 transition-colors ${
                        isCartPage
                          ? 'bg-green-600 text-white active:bg-green-700'
                          : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                      }`}
                      aria-label="Carrito"
                    >
                      <ShoppingCart size={20} />
                      {cartCount > 0 && (
                        <span className={`absolute -top-1.5 -right-1.5 flex items-center justify-center rounded-full text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 ${
                          isCartPage ? 'bg-red-500' : 'bg-green-600'
                        }`}>
                          {cartCount > 99 ? '99+' : cartCount}
                        </span>
                      )}
                    </Link>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
